import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { createWebCallSession, webCallSocketUrl } from '@/reducers/voiceAgents/voiceAgents.actions'

// Cartesia's agent socket echoes audio back in whatever `input_format` the
// stream declared, so one rate governs capture and playback. Only these PCM
// rates are accepted upstream — notably NOT 48000, which is what Firefox and
// Safari usually hand back — hence the resampling worklet below.
const SUPPORTED_RATES = [16000, 24000, 44100]
const PREFERRED_RATE = 16000

// 40 ms per frame at the negotiated rate: inside Cartesia's 20–100 ms guidance,
// and few enough messages/sec to keep the socket quiet.
const FRAME_MS = 40

// Level metering for the call orb. 512 samples is ~32 ms at 16 kHz — long
// enough that the RMS doesn't jitter on individual glottal pulses, short enough
// that the orb reacts inside one syllable.
const ANALYSER_FFT = 512

// Speech RMS sits around 0.02–0.2, so a raw reading would leave the orb nearly
// static. Scaled and clamped to 0–1, which is the range the orb's CSS expects.
const LEVEL_GAIN = 5

const rmsOf = (analyser, buffer) => {
  if (!analyser) {
    return 0
  }
  analyser.getFloatTimeDomainData(buffer)
  let sum = 0
  for (let i = 0; i < buffer.length; i += 1) {
    sum += buffer[i] * buffer[i]
  }
  return Math.min(1, Math.sqrt(sum / buffer.length) * LEVEL_GAIN)
}

// Capture worklet. Runs on the audio thread: linearly resamples the mic from
// the AudioContext's rate down to the negotiated rate, converts to signed
// 16-bit PCM, and posts fixed-size frames back to the main thread.
//
// Shipped as a Blob URL rather than a separate asset so it needs no webpack
// entry — `addModule` only takes a URL, and a static file would have to be
// copied and versioned alongside the bundle.
const CAPTURE_WORKLET = `
class CaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super()
    const { targetRate, frameSize } = options.processorOptions
    this.step = sampleRate / targetRate
    this.cursor = 0
    this.pending = new Float32Array(0)
    this.out = new Int16Array(frameSize)
    this.outLen = 0
  }

  process(inputs) {
    const chunk = inputs[0] && inputs[0][0]
    if (!chunk) {
      return true
    }

    const merged = new Float32Array(this.pending.length + chunk.length)
    merged.set(this.pending, 0)
    merged.set(chunk, this.pending.length)

    let cursor = this.cursor
    while (cursor + 1 < merged.length) {
      const i = Math.floor(cursor)
      const frac = cursor - i
      const sample = merged[i] * (1 - frac) + merged[i + 1] * frac
      const clamped = Math.max(-1, Math.min(1, sample))
      this.out[this.outLen++] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
      if (this.outLen === this.out.length) {
        const frame = this.out.slice()
        this.port.postMessage(frame.buffer, [frame.buffer])
        this.outLen = 0
      }
      cursor += this.step
    }

    const consumed = Math.floor(cursor)
    this.pending = merged.slice(consumed)
    this.cursor = cursor - consumed
    return true
  }
}
registerProcessor('web-call-capture', CaptureProcessor)
`

const bytesToBase64 = (bytes) => {
  // Chunked so a long frame can't blow the argument limit on String.fromCharCode.
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

const base64ToBytes = (b64) => {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Ask for the preferred rate, but accept whatever the browser actually gives —
// Chrome honours the hint, Firefox/Safari often don't. If the resulting rate
// isn't one Cartesia accepts, we keep the context as-is and let the worklet
// resample to PREFERRED_RATE.
const openAudioContext = () => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  let context
  try {
    context = new AudioContextCtor({ sampleRate: PREFERRED_RATE })
  } catch {
    context = new AudioContextCtor()
  }
  const negotiatedRate = SUPPORTED_RATES.includes(context.sampleRate)
    ? context.sampleRate
    : PREFERRED_RATE
  return { context, negotiatedRate }
}

// Drives a live browser call with a Studio agent (AVR-532).
//
// Wire shape: an authenticated POST mints a single-use ticket, the socket
// redeems it, and the voice-agents BE proxies to Cartesia — the Cartesia key
// never reaches the browser. This hook owns everything downstream of that:
// mic capture, PCM framing, playback scheduling and teardown.
export const useWebCall = () => {
  const dispatch = useDispatch()
  const [status, setStatus] = useState('idle') // idle | connecting | live | ended | error
  const [error, setError] = useState(null)
  const [isMuted, setIsMuted] = useState(false)
  // The conversation so far, oldest first. Unlike the audio levels this is
  // rendered, so it has to be state — but it only changes on a turn boundary
  // or a token, a handful of times a second, not per animation frame.
  const [turns, setTurns] = useState([])
  // Cartesia's id for this call, from the ready frame. The only handle on a
  // call an operator just made — Call Explorer won't have it for a while, so
  // Studio shows it in the meantime.
  const [callId, setCallId] = useState(null)

  const socketRef = useRef(null)
  const contextRef = useRef(null)
  const streamRef = useRef(null)
  const workletRef = useRef(null)
  const sourceRef = useRef(null)
  const workletUrlRef = useRef(null)
  const mutedRef = useRef(false)
  const rateRef = useRef(PREFERRED_RATE)
  // Whether the agent ever went live, read synchronously from socket callbacks
  // where the `status` state would be a render behind.
  const liveRef = useRef(false)
  // Playback cursor: agent audio arrives faster than real time, so each chunk
  // is scheduled to start where the previous one ends rather than immediately.
  const nextStartRef = useRef(0)
  const playingRef = useRef(new Set())
  // Metering taps, read by the orb through `getLevels` rather than pushed into
  // React state: at 60 fps a `setState` per frame would re-render the whole
  // dock, and nothing outside the orb's own animation needs the number.
  const inputAnalyserRef = useRef(null)
  const outputAnalyserRef = useRef(null)
  const outputBusRef = useRef(null)
  const levelBufRef = useRef(new Float32Array(ANALYSER_FFT))

  // Turns are addressed by id and arrive out of order relative to each other —
  // a user turn can end while the assistant's is still streaming — so every
  // update is an upsert on the id rather than an append or a tail edit.
  // `patch` may be a function of the existing turn, which is what lets a delta
  // append to text it hasn't read yet — reading `turns` here instead would
  // capture a stale array between two tokens of the same turn.
  const upsertTurn = useCallback((id, role, patch = {}) => {
    const EMPTY = { id, role, text: '', isFinal: false, wasInterrupted: false, toolCalls: [] }
    setTurns((current) => {
      const index = current.findIndex((turn) => turn.id === id)
      const existing = index === -1 ? EMPTY : current[index]
      const applied = typeof patch === 'function' ? patch(existing) : patch
      const updated = { ...existing, ...applied }
      if (index === -1) {
        return [...current, updated]
      }
      const next = [...current]
      next[index] = updated
      return next
    })
  }, [])

  const stopPlayback = useCallback(() => {
    playingRef.current.forEach((source) => {
      try {
        source.stop()
      } catch {
        // Already ended — nothing to stop.
      }
    })
    playingRef.current.clear()
    nextStartRef.current = 0
  }, [])

  const teardown = useCallback(() => {
    stopPlayback()

    const socket = socketRef.current
    socketRef.current = null
    if (socket) {
      // Tell the BE first so it can close the Cartesia leg cleanly rather than
      // waiting for the socket to drop.
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ event: 'stop' }))
      }
      socket.onclose = null
      socket.close()
    }

    if (workletRef.current) {
      workletRef.current.port.onmessage = null
      workletRef.current.disconnect()
      workletRef.current = null
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }
    // Releases the browser's mic indicator — leaving this on is the most
    // visible way a demo tool can look broken.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (contextRef.current) {
      contextRef.current.close().catch(() => {})
      contextRef.current = null
    }
    if (workletUrlRef.current) {
      URL.revokeObjectURL(workletUrlRef.current)
      workletUrlRef.current = null
    }
    // Dropped after the context closes: the orb's animation frame can still fire
    // once more, and reading a detached analyser would throw.
    inputAnalyserRef.current = null
    outputAnalyserRef.current = null
    outputBusRef.current = null
    mutedRef.current = false
    liveRef.current = false
    setIsMuted(false)
  }, [stopPlayback])

  const playChunk = useCallback((payload) => {
    const context = contextRef.current
    if (!context) {
      return
    }
    const bytes = base64ToBytes(payload)
    // Guard a truncated frame: a stray odd byte would misalign every sample.
    const sampleCount = Math.floor(bytes.length / 2)
    if (!sampleCount) {
      return
    }
    const pcm = new Int16Array(bytes.buffer, 0, sampleCount)
    const samples = new Float32Array(sampleCount)
    for (let i = 0; i < sampleCount; i += 1) {
      samples[i] = pcm[i] / 0x8000
    }

    // Built at the negotiated rate; if the context runs at a different rate the
    // Web Audio graph resamples on playback for us.
    const buffer = context.createBuffer(1, sampleCount, rateRef.current)
    buffer.copyToChannel(samples, 0)
    const source = context.createBufferSource()
    source.buffer = buffer
    // Through the metering bus, not straight to the speakers: every agent chunk
    // is its own short-lived BufferSource, so tapping each one individually
    // would mean wiring and tearing down an analyser 25 times a second.
    source.connect(outputBusRef.current || context.destination)

    const startAt = Math.max(context.currentTime, nextStartRef.current)
    source.start(startAt)
    nextStartRef.current = startAt + buffer.duration
    playingRef.current.add(source)
    source.onended = () => playingRef.current.delete(source)
  }, [])

  const start = useCallback(
    async (agentId, { metadata = {}, callerNumber = null } = {}) => {
      if (status === 'connecting' || status === 'live') {
        return
      }
      setError(null)
      setStatus('connecting')
      // A new call is a new conversation. Clearing on `start` rather than on
      // `reset` means the transcript of the last call survives hanging up —
      // there is nothing else to read it from afterwards.
      setTurns([])
      setCallId(null)

      let session
      try {
        session = await dispatch(createWebCallSession(agentId, { metadata, callerNumber }))
        if (!session?.ticket) {
          // The thunk already surfaced the reason as a toast.
          setStatus('idle')
          return
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            // Without AEC the agent hears itself through the laptop speakers
            // and barges in on its own voice.
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        })
        streamRef.current = stream

        const { context, negotiatedRate } = openAudioContext()
        contextRef.current = context
        rateRef.current = negotiatedRate
        if (context.state === 'suspended') {
          await context.resume()
        }

        // One bus for everything the agent says: chunks → gain → analyser →
        // speakers. The gain node is the stable connection point the per-chunk
        // sources attach to.
        const outputBus = context.createGain()
        const outputAnalyser = context.createAnalyser()
        outputAnalyser.fftSize = ANALYSER_FFT
        outputBus.connect(outputAnalyser)
        outputAnalyser.connect(context.destination)
        outputBusRef.current = outputBus
        outputAnalyserRef.current = outputAnalyser

        const workletUrl = URL.createObjectURL(
          new Blob([CAPTURE_WORKLET], { type: 'application/javascript' })
        )
        workletUrlRef.current = workletUrl
        await context.audioWorklet.addModule(workletUrl)

        const socket = new WebSocket(webCallSocketUrl(session.streamPath, session.ticket))
        socketRef.current = socket

        socket.onopen = () => {
          socket.send(JSON.stringify({ event: 'start', sample_rate: negotiatedRate }))
        }

        socket.onmessage = (event) => {
          const data = JSON.parse(event.data)
          if (data.event === 'ready') {
            setCallId(data.callId || null)
            // Only wire the mic once the agent is live, so the operator's
            // opening words can't land on a half-open stream.
            const node = new AudioWorkletNode(context, 'web-call-capture', {
              processorOptions: {
                targetRate: negotiatedRate,
                frameSize: Math.round((negotiatedRate * FRAME_MS) / 1000),
              },
            })
            node.port.onmessage = ({ data: frame }) => {
              if (mutedRef.current || socket.readyState !== WebSocket.OPEN) {
                return
              }
              socket.send(
                JSON.stringify({
                  event: 'media_input',
                  media: { payload: bytesToBase64(new Uint8Array(frame)) },
                })
              )
            }
            const micSource = context.createMediaStreamSource(stream)
            micSource.connect(node)
            // A second tap off the same source, for the orb. An analyser is a
            // pass-through with no output connected, so this measures the mic
            // without routing it anywhere.
            const inputAnalyser = context.createAnalyser()
            inputAnalyser.fftSize = ANALYSER_FFT
            micSource.connect(inputAnalyser)
            inputAnalyserRef.current = inputAnalyser
            // Not connected to destination: routing the mic to the speakers
            // would echo the operator back at themselves.
            sourceRef.current = micSource
            workletRef.current = node
            liveRef.current = true
            setStatus('live')
          } else if (data.event === 'media_output') {
            playChunk(data.media.payload)
          } else if (data.event === 'clear') {
            // Barge-in: the agent stopped talking, so drop what we queued.
            stopPlayback()
          } else if (data.event === 'turn_started') {
            upsertTurn(data.turn.id, data.turn.role, {})
          } else if (data.event === 'turn_delta') {
            // Assistant only — a user turn has no partial text to stream, so
            // it materialises whole on turn_ended below.
            upsertTurn(data.turn.id, data.turn.role, (turn) => ({
              text: turn.text + data.turn.text,
            }))
          } else if (data.event === 'turn_ended') {
            // The BE's final text replaces whatever the deltas assembled: a
            // barge-in truncates the spoken turn, and the deltas we received
            // can run past what the caller actually heard.
            upsertTurn(data.turn.id, data.turn.role, {
              text: data.turn.text,
              isFinal: true,
              wasInterrupted: data.turn.wasInterrupted,
              toolCalls: data.turn.toolCalls || [],
            })
          } else if (data.event === 'error') {
            setError(data.message || 'Call failed.')
            setStatus('error')
            teardown()
          }
        }

        socket.onerror = () => {
          setError('Lost connection to the agent.')
          setStatus('error')
          teardown()
        }

        socket.onclose = (event) => {
          // Closing before the agent ever went live is a failure, not a
          // finished call — reporting "Call ended" there sends you looking in
          // the wrong place. The BE puts the reason in the close frame.
          const wasLive = liveRef.current
          teardown()
          if (!wasLive) {
            setError((existing) => existing || event?.reason || 'The call could not be started.')
          }
          setStatus((current) => {
            if (current === 'error') {
              return current
            }
            return wasLive ? 'ended' : 'error'
          })
        }
      } catch (caught) {
        const message =
          caught?.name === 'NotAllowedError'
            ? 'Microphone access was blocked. Allow it in your browser and try again.'
            : caught?.message || 'Could not start the web call.'
        setError(message)
        setStatus('error')
        teardown()
      }
    },
    [dispatch, playChunk, status, stopPlayback, teardown, upsertTurn]
  )

  const stop = useCallback(() => {
    teardown()
    setStatus((current) => (current === 'error' ? current : 'ended'))
  }, [teardown])

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current
    setIsMuted(mutedRef.current)
  }, [])

  const reset = useCallback(() => {
    teardown()
    setError(null)
    setStatus('idle')
    // Everything, including what was said. `reset` is what leaving the call
    // view calls, and the view promises the conversation does not outlive it —
    // clearing only on the next `start` left the last call's transcript on
    // screen when you came back.
    setTurns([])
    setCallId(null)
  }, [teardown])

  // Current loudness of each leg, 0–1. Pull-based: the orb asks once per
  // animation frame, which is exactly as often as it can use the answer.
  //
  // Muting zeroes the input leg. The analyser still hears the mic — mute only
  // stops us *sending* frames — but an orb that reacts to a muted operator is
  // telling them the agent can hear them, which is the one thing mute promises
  // it can't.
  const getLevels = useCallback(
    () => ({
      input: mutedRef.current ? 0 : rmsOf(inputAnalyserRef.current, levelBufRef.current),
      output: rmsOf(outputAnalyserRef.current, levelBufRef.current),
    }),
    []
  )

  // Closing the modal or navigating away must not leave a live mic and an
  // open Cartesia leg behind.
  useEffect(() => teardown, [teardown])

  return { status, error, isMuted, turns, callId, start, stop, toggleMute, reset, getLevels }
}
